import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AlertService, MessageSeverity } from '../../services/alert.service';
import { DEFAULT_LEVEL_NAMES, DistributorSetupService, LevelNames, SetupHolder, SetupProductOption, SetupTerritorySplit, apiError, dayDate, dayText } from './distributor-setup.service';

interface ShareRow { businessUnitId: number; businessUnitName: string; defaultPercent: number; percent: number; }

/**
 * Adds or edits one business unit split: how one brick's part is shared between the business units of a line holding it
 * (the level that holds the sales bricks, named as configured, e.g. Territory), from a date (to a date), for all products or one brand / item - of one distributor brick, or (no
 * distributor brick) of every distributor brick for that product. The business units are the brick's sales-brick
 * holders on the start date; the shares must add up to 99.95-100.
 * The inputs are only read when they change (bound arrays must keep their reference): the form is filled again only
 * when another split is given.
 */
@Component({
  selector: 'crm-territory-split-editor',
  templateUrl: './territory-split-editor.component.html'
})
export class TerritorySplitEditorComponent implements OnInit, OnChanges {
  @Input() distributorBrickId: number;
  /** bricks to choose from (when the brick is not fixed) */
  @Input() bricks: { id: number; name: string }[] = [];
  @Input() fixedBrickId: number;
  @Input() fixedLineId: number;
  @Input() products: SetupProductOption[] = [];
  @Input() requireProduct = false;
  /** the split being edited (null = a new one) */
  @Input() split: SetupTerritorySplit;
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  brickId: number;
  productId: number = null;
  start: Date;
  end: Date;
  lines: { id: number; name: string }[] = [];
  lineId: number;
  rows: ShareRow[] = [];
  holders: SetupHolder[] = [];
  loading = false;
  saving = false;
  error = '';
  names: LevelNames = DEFAULT_LEVEL_NAMES;
  productOptions: { label: string; value: number }[] = [];
  brickOptions: { label: string; value: number }[] = [];
  private filled = false;

  constructor(private service: DistributorSetupService, private alertService: AlertService) { }

  ngOnInit() {
    this.service.levelNames().subscribe(n => this.names = n || DEFAULT_LEVEL_NAMES);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.products || changes.requireProduct) {
      const all = this.requireProduct ? [] : [{ label: 'All products', value: null }];
      this.productOptions = all.concat((this.products || []).map(p => ({ label: (p.id < 0 ? 'Brand: ' : 'Item: ') + p.name, value: p.id })));
    }
    if (changes.bricks) this.brickOptions = (this.bricks || []).map(b => ({ label: b.name, value: b.id }));
    // (re)fill the form only for another split, never on a change of the lists (that would wipe what is being typed)
    if (!this.filled || changes.split) this.fill();
  }

  private fill() {
    this.filled = true;
    this.error = '';
    if (this.split) {
      this.brickId = this.split.brickId;
      this.productId = this.split.product ? (this.split.product.itemId || (this.split.product.itemBrandId ? -this.split.product.itemBrandId : null)) : null;
      this.start = dayDate(this.split.start);
      this.end = dayDate(this.split.end);
      this.lineId = this.split.lineId;
      this.rows = this.split.shares.map(s => ({ businessUnitId: s.businessUnitId, businessUnitName: s.businessUnitName, defaultPercent: s.defaultPercent, percent: s.percent }));
      this.lines = [{ id: this.split.lineId, name: this.split.lineName }];
    } else {
      this.brickId = this.fixedBrickId;
      this.productId = null;
      this.start = null;
      this.end = null;
      this.rows = [];
      this.lines = [];
      this.lineId = this.fixedLineId;
    }
  }

  /** the business units holding the brick on the start date (existing shares kept) */
  loadTerritories() {
    if (!this.brickId || !this.start) { this.error = 'Choose the brick and the start date first.'; return; }
    this.error = '';
    this.loading = true;
    this.service.holders(this.brickId, dayText(this.start)).subscribe(holders => {
      this.loading = false;
      this.holders = holders || [];
      const lineIds = Array.from(new Set(this.holders.map(h => h.lineId)));
      this.lines = lineIds.map(id => ({ id, name: this.holders.find(h => h.lineId == id).lineName }));
      if (this.fixedLineId) this.lineId = this.fixedLineId;
      else if (!lineIds.includes(this.lineId)) this.lineId = lineIds.length ? lineIds[0] : null;
      this.fillRows();
      if (!this.holders.length) this.error = `No ${this.names.holder.toLowerCase()} holds this brick as a sales brick on the start date.`;
    }, err => { this.loading = false; this.error = apiError(err, `Could not load the ${this.names.holders.toLowerCase()}.`); });
  }

  fillRows() {
    const old = this.rows;
    this.rows = this.holders.filter(h => h.lineId == this.lineId).map(h => {
      const had = old.find(r => r.businessUnitId == h.businessUnitId);
      return { businessUnitId: h.businessUnitId, businessUnitName: h.businessUnitName, defaultPercent: h.percent, percent: had ? had.percent : h.percent };
    });
  }

  get total(): number { return Math.round(this.rows.reduce((s, r) => s + (Number(r.percent) || 0), 0) * 100) / 100; }

  save() {
    if (!this.brickId || !this.start) { this.error = 'The brick and the start date are required.'; return; }
    if (this.requireProduct && this.productId == null) { this.error = 'Choose a brand or an item.'; return; }
    if (!this.rows.length) { this.error = `Load the ${this.names.holders.toLowerCase()} first.`; return; }
    this.error = '';
    this.saving = true;
    const body = {
      distributorBrickId: this.distributorBrickId || null,
      itemId: this.productId > 0 ? this.productId : null,
      itemBrandId: this.productId < 0 ? -this.productId : null,
      brickId: this.brickId,
      start: dayText(this.start),
      end: this.end ? dayText(this.end) : null,
      shares: this.rows.map(r => ({ businessUnitId: r.businessUnitId, percent: Number(r.percent) || 0 })),
      replaceIds: this.split ? this.split.ids : []
    };
    this.service.saveTerritorySplit(body).subscribe(() => {
      this.saving = false;
      this.alertService.showMessage('', 'Split saved', MessageSeverity.Success);
      this.saved.emit();
    }, err => { this.saving = false; this.error = apiError(err, 'The split could not be saved.'); });
  }
}
